<template>
  <div>
    <DashboardHeader title="Invite Bizness">
      <div class="px-4 py-4">
        <v-row>
          <v-col :cols="isMobile ? 12 : 3">
            <vAlertHeader title="Upload Data" color="blue"></vAlertHeader>
            <vXLSX @json-data="getJsonData"></vXLSX>
            <v-btn
              class="ml-4 mb-4"
              dark
              color="blue"
              @click="uploadBusinesses()"
              >Upload Business Now</v-btn
            >

            <div class="bulk-capture-container">
              <!-- Header Section -->
              <div
                class="header-section"
                :style="{
                  background: `linear-gradient(135deg, #a1a5b6 0%, ${getThemeColor} 100%)`,
                }"
              >
                <div class="header-content">
                  <div class="header-text">
                    <h1 class="main-title">Business Capture</h1>
                    <p class="subtitle">Capture New Business</p>
                  </div>
                  <div class="header-icon" v-if="!isMobile">
                    <v-icon size="48" color="primary"
                      >mdi-account-multiple-plus</v-icon
                    >
                  </div>
                </div>
              </div>

              <!-- Form Section -->
              <v-card class="form-card mb-6" elevation="2">
                <div class="card-header">
                  <h2 class="form-title">Add New Business</h2>
                  <v-chip color="primary" outlined small class="status-chip">
                    <v-icon left small>mdi-check</v-icon>
                    Fill In
                  </v-chip>
                </div>

                <v-card-text class="px-6 pb-4">
                  <v-row class="form-row">
                    <v-col
                      v-for="(column, i) in columns"
                      :key="i"
                      :cols="isMobile ? 12 : 6"
                      :md="column === 'email' ? 12 : 6"
                      class="form-col"
                    >
                      <div class="input-wrapper">
                        <v-text-field
                          :label="getFieldLabel(column)"
                          :placeholder="getFieldLabel(column)"
                          :prepend-inner-icon="getFieldIcon(column)"
                          :type="getFieldType(column)"
                          outlined
                          dense
                          hide-details="auto"
                          class="modern-input"
                          v-model="biz[column]"
                          color="primary"
                        ></v-text-field>
                      </div>
                    </v-col>
                  </v-row>

                  <div class="form-actions">
                    <v-btn
                      color="primary"
                      large
                      :block="isMobile"
                      :loading="isSubmitting"
                      @click="captureBusiness"
                      class="submit-btn"
                    >
                      <v-icon left>mdi-account-plus</v-icon>
                      Add Business
                    </v-btn>
                    <v-btn
                      outlined
                      large
                      :block="isMobile"
                      @click="clearForm"
                      class="clear-btn ml-3"
                      :class="{ 'mt-3': isMobile }"
                    >
                      <v-icon left>mdi-refresh</v-icon>
                      Clear Form
                    </v-btn>
                  </div>
                </v-card-text>
              </v-card>
            </div></v-col
          >
          <v-col :cols="isMobile ? 12 : 9">
            <!-- Members List Section -->
            <v-card
              class="members-card"
              elevation="0"
              v-if="biznesses.length > 0"
            >
              <div class="card-header">
                <div>
                  <h2 class="members-title">Businesses Yet To Be Invited</h2>
                  <p class="members-subtitle">
                    {{ biznesses.length }} Businesses{{
                      biznesses.length !== 1 ? "s" : ""
                    }}
                  </p>
                </div>
                <v-chip
                  :color="biznesses.length > 0 ? 'success' : 'grey'"
                  outlined
                  class="count-chip"
                >
                  <v-icon left small>mdi-account-group</v-icon>
                  {{ biznesses.length }}
                </v-chip>
              </div>

              <!-- Search Bar -->
              <div class="search-section" v-if="biznesses.length > 0">
                <v-text-field
                  v-model="search"
                  prepend-inner-icon="mdi-magnify"
                  label="Search members..."
                  outlined
                  dense
                  hide-details
                  clearable
                  class="search-input"
                  color="primary"
                ></v-text-field>
                <v-btn
                  @click="inviteSelectedBusinessesNow"
                  class="mt-2"
                  :color="getThemeColor"
                  dark
                  >Invite Businesses Now</v-btn
                >
              </div>

              <!-- Data Table -->
              <div class="table-container">
                <v-data-table
                  v-model="selectedBusinesses"
                  show-select
                  :headers="enhancedHeaders"
                  :items="biznesses"
                  :search="search"
                  :mobile-breakpoint="768"
                  hide-default-footer
                  :items-per-page="10"
                  class="modern-table"
                  :class="{ 'mobile-table': isMobile }"
                >
                  <!-- Desktop table cells -->
                  <template v-slot:item.name="{ item }">
                    <div class="name-cell">
                      <v-avatar size="32" color="primary" class="mr-3">
                        <span class="white--text">{{
                          getInitials(item.name)
                        }}</span>
                      </v-avatar>
                      <div>
                        <v-icon small class="mr-1"
                          >mdi-google-my-business</v-icon
                        ><v-text-field
                          @blur="updateFieldValue('name', item)"
                          v-model="item.name"
                        ></v-text-field>
                      </div>
                    </div>
                  </template>

                  <template v-slot:item.pic="{ item }">
                    <div class="contact-cell">
                      <v-icon small class="mr-1">mdi-account-outline</v-icon>
                      <v-text-field
                        @blur="updateFieldValue('pic', item)"
                        v-model="item.pic"
                      ></v-text-field>
                    </div>
                  </template>

                  <template v-slot:item.phone="{ item }">
                    <div class="contact-cell">
                      <v-icon small class="mr-1">mdi-phone</v-icon>
                      <v-text-field
                        @blur="updateFieldValue('phone', item)"
                        v-model="item.phone"
                      ></v-text-field>
                    </div>
                  </template>

                  <template v-slot:item.email="{ item }">
                    <div class="contact-cell">
                      <v-icon small class="mr-1">mdi-email</v-icon>
                      <v-text-field
                        @blur="updateFieldValue('email', item)"
                        v-model="item.email"
                      ></v-text-field>
                    </div>
                  </template>

                  <template v-slot:item.city="{ item }">
                    <div class="contact-cell">
                      <v-icon small class="mr-1">mdi-home-city</v-icon>
                      <v-text-field
                        @blur="updateFieldValue('city', item)"
                        v-model="item.city"
                      ></v-text-field>
                    </div>
                  </template>

                  <!-- Empty state -->
                  <template v-slot:no-data>
                    <div class="empty-state">
                      <v-icon size="64" color="grey lighten-2"
                        >mdi-account-plus</v-icon
                      >
                      <h3 class="grey--text mt-4">No members added yet</h3>
                      <p class="grey--text">
                        Start by adding your first member above
                      </p>
                    </div>
                  </template>
                </v-data-table>
              </div>
            </v-card></v-col
          >
        </v-row>
      </div>
    </DashboardHeader>
  </div>
</template>

<script>
import axios from "@/plugins/axios";
import helpersJs from "@/plugins/helpers";
import DashboardHeader from "@/components/DashboardHeader.vue";
import vAlertHeader from "@/components/Legos/vAlertHeader.vue";
import vXLSX from "@/components/Legos/vXLSX.vue";
export default {
  components: { vAlertHeader, DashboardHeader, vXLSX },
  computed: {
    isMobile() {
      return this.$store.getters.isMobile;
    },
    getAccessToken() {
      return this.$store.getters.getAccessToken;
    },
    getThemeColor() {
      return this.$store.getters.getThemeColor;
    },
    enhancedHeaders() {
      const baseHeaders = [
        {
          text: "Name",
          value: "name",
          icon: "mdi-google-my-business",
          class: "name-header",
        },
        {
          text: "Person In Charge",
          value: "pic",
          icon: "mdi-account-outline",
        },

        {
          text: "Mobile",
          value: "phone",
          icon: "mdi-phone",
        },
        {
          text: "Email",
          value: "email",
          icon: "mdi-email",
        },
        {
          text: "City",
          value: "city",
          icon: "mdi-home-city",
        },
      ];
      return baseHeaders;
    },
  },
  data: () => ({
    selectedBusinesses: [],
    isSubmitting: false,
    search: "",
    columns: [
      "name",
      "pic",
      "phone",
      "email",
      "address",
      "city",
      "postal_code",
      "industry",
    ],
    biz: {
      name: null,
      pic: null,
      phone: null,
      email: null,
      address: null,
      city: "Durban",
      industry: "General",
      postal_code: "4001",
    },
    biznesses: [],
  }),
  mounted() {
    this.getAllBusinessesToInvite();
  },
  methods: {
    inviteSelectedBusinessesNow() {
      if (this.selectedBusinesses.length < 1) {
        this.$toast.error("Select at least 1 business to invite", {
          duration: 5000,
        });
        return false;
      }

      this.$store.commit("setLoader", true);
      axios
        .postDataWithToken(
          "/auth/invite-new-business",
          { selectedBusinesses: this.selectedBusinesses },
          this.getAccessToken
        )
        .then((res) => {
          this.$store.commit("setLoader", false);
          if (res && res.status == "success") {
            this.$toast.success(res.message, { duration: 5000 });
          } else {
            this.$toast.error(res.message, { duration: 5000 });
          }
        });
    },
    async updateFieldValue(column, item) {
      const value = item[column];
      await helpersJs.updateModelSingleRow(
        "businesses_to_invite",
        column,
        item.id,
        value
      );
      const col = this.capitalizeFirstCharacter(column);
      this.$toast.success(col + " updated successfully!", { duration: 5000 });
    },
    getJsonData(json) {
      const biznesses = [];
      json.forEach((business) => {
        const biz = {};
        biz.name = business["Doctor name"];
        biz.pic = business["Doctor name"];
        biz.phone = business["Cell Number"];
        biz.email = business["Email"];
        biz.address = business["Doctor's Address"];
        biz.city = business["City"];
        biz.industry = "Private Health";

        biznesses.push(biz);
      });
      console.log(biznesses);
      this.biznesses = biznesses;
    },
    getAllBusinessesToInvite() {
      this.$store.commit("setLoader", true);
      axios
        .getData("/auth/get-all-businesses-to-invite", this.getAccessToken)
        .then((res) => {
          this.$store.commit("setLoader", false);
          this.biznesses = res;
        });
    },
    getInputValue(value, column) {
      this.biz[column] = value;
    },
    capitalizeFirstCharacter(data) {
      return helpersJs.capitalizeFirstCharacter(data);
    },
    getInitials(firstName, lastName) {
      const first = firstName ? firstName.charAt(0).toUpperCase() : "";
      const last = lastName ? lastName.charAt(0).toUpperCase() : "";
      return first + last;
    },
    getFieldLabel(column) {
      const labels = {
        name: "Business Name",
        pic: "Person In Charge",
        phone: "Mobile Number",
        email: "Business Email",
        address: "Physical Address",
        city: "City",
        industry: "Industry",
        postal_code: "Postal Code",
      };
      return labels[column] || this.capitalizeFirstCharacter(column);
    },
    getFieldIcon(column) {
      const icons = {
        name: "mdi-google-my-business",
        pic: "mdi-account-outline",
        city: "mdi-home-city",
        phone: "mdi-phone",
        email: "mdi-email",
        address: "mdi-map-marker",
      };
      return icons[column] || "mdi-text";
    },
    getFieldType(column) {
      const types = {
        email: "email",
        phone: "tel",
      };
      return types[column] || "text";
    },
    getFieldRules(column) {
      const rules = {
        name: [(v) => !!v || "Business name is required"],
        email: [
          (v) => !v || /.+@.+\..+/.test(v) || "Business Email must be valid",
        ],
        phone: [
          (v) =>
            !v || /^[0-9+\-\s()]+$/.test(v) || "Mobile number must be valid",
        ],
      };
      return rules[column] || [];
    },
    clearForm() {
      this.biz = {
        name: null,
        pic: null,
        phone: null,
        email: null,
        address: null,
        city: "Durban",
        industry: "General",
      };
    },
    uploadBusinesses() {
      if (this.biznesses.length < 1) {
        this.$toast.error("No business data found!", { duration: 5000 });
        return false;
      }
      this.$store.commit("setLoader", true);
      axios
        .postDataWithToken(
          "/auth/upload-businesses-to-invite-from-xlsx",
          { biznesses: this.biznesses },
          this.getAccessToken
        )
        .then((res) => {
          this.$store.commit("setLoader", false);
          if (res && res.status == "success") {
            this.$toast.success("Businesses uploaded successfully!", {
              duration: 5000,
            });
            this.getAllBusinessesToInvite();
          } else {
            this.$toast.error(res.message, { duration: 5000 });
          }
        });
    },
    captureBusiness() {
      if (!this.biz.name || !this.biz.phone || !this.biz.email) {
        this.$toast.error("Business name, phone and emails are all required!", {
          duration: 5000,
        });
        return false;
      }
      this.$store.commit("setLoader", true);
      axios
        .postDataWithToken(
          "/auth/capture-business-to-invite",
          { biz: this.biz },
          this.getAccessToken
        )
        .then((res) => {
          this.$store.commit("setLoader", false);
          if (res && res.status == "success") {
            this.biznesses.push(res.business);
            this.$toast.success("Business captured successfully!", {
              duration: 5000,
            });
          } else {
            this.$toast.error(res.message, { duration: 5000 });
          }
        });
    },
  },
};
</script>

<style scoped>
.bulk-capture-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 16px;
}

.header-section {
  margin-bottom: 24px;
  padding: 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  color: white;

  .header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .header-text {
      .main-title {
        font-size: 2rem;
        font-weight: 600;
        margin-bottom: 8px;
        line-height: 1.2;
      }

      .subtitle {
        font-size: 1.1rem;
        opacity: 0.9;
        margin: 0;
      }
    }

    .header-icon {
      opacity: 0.8;
    }
  }
}

.form-card,
.members-card {
  border-radius: 16px;
  border: 1px solid #e0e0e0;

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 6px 24px 4px;
    border-bottom: 1px solid #f5f5f5;

    .form-title,
    .members-title {
      font-size: 1.5rem;
      font-weight: 600;
      color: #333;
      margin-bottom: 4px;
    }

    .members-subtitle {
      color: #666;
      font-size: 0.9rem;
      margin: 0;
    }

    .status-chip,
    .count-chip {
      font-weight: 500;
    }
  }
}

.form-row {
  margin: 0;

  .form-col {
    padding: 8px;
  }

  .input-wrapper {
    .modern-input {
      ::v-deep .v-input__control {
        .v-input__slot {
          border-radius: 12px;
          transition: all 0.3s ease;

          &:hover {
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          }
        }
      }
    }
  }
}

.form-actions {
  display: flex;
  justify-content: flex-start;
  margin-top: 6px;
  flex-wrap: wrap;

  .submit-btn {
    border-radius: 12px;
    font-weight: 600;
    text-transform: none;
    font-size: 1rem;
  }

  .clear-btn {
    border-radius: 12px;
    font-weight: 500;
    text-transform: none;
  }
}

.search-section {
  padding: 16px 24px;
  background: #fafafa;

  .search-input {
    ::v-deep .v-input__control {
      .v-input__slot {
        border-radius: 12px;
        background: white;
      }
    }
  }
}

.table-container {
  .modern-table {
    ::v-deep .v-data-table__wrapper {
      border-radius: 0 0 16px 16px;

      .table-header {
        background: #f8f9fa;

        .header-cell {
          padding: 16px;
          font-weight: 600;
          color: #333;
          border-bottom: 2px solid #e9ecef;

          .header-content {
            display: flex;
            align-items: center;
          }
        }
      }

      tbody tr {
        transition: background-color 0.2s ease;

        &:hover {
          background-color: #f8f9fa;
        }

        td {
          padding: 16px;
          border-bottom: 1px solid #e9ecef;
        }
      }
    }
  }

  .name-cell {
    display: flex;
    align-items: center;
  }

  .contact-cell {
    display: flex;
    align-items: center;
    color: #666;
  }
}

.member-card-mobile {
  margin: 8px 16px;
  border-radius: 12px;
  border: 1px solid #e0e0e0;

  .member-info {
    .member-name {
      display: flex;
      align-items: center;
      font-size: 1.1rem;
      margin-bottom: 12px;
    }

    .member-details {
      display: flex;
      flex-direction: column;
      gap: 8px;

      .detail-item {
        display: flex;
        align-items: center;
        color: #666;
        font-size: 0.9rem;
      }
    }
  }
}

.empty-state {
  padding: 48px 24px;
  text-align: center;
}

@media (max-width: 768px) {
  .bulk-capture-container {
    padding: 8px;
  }

  .header-section {
    padding: 16px;
    margin-bottom: 16px;

    .header-content {
      flex-direction: column;
      text-align: center;
      gap: 16px;

      .header-text .main-title {
        font-size: 1.6rem;
      }
    }
  }

  .form-card .card-header,
  .members-card .card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }

  .search-section {
    padding: 12px 16px;
  }
}
</style>
